import { cva, type VariantProps } from 'class-variance-authority';
import { focusRing, transitionBase, uiMarker } from './shared';

/**
 * SIDEBAR NAVIGATION
 *
 * The row a sidebar is made of: an icon, a label, and sometimes something on
 * the right. The app had two of these — the main sidebar and the settings
 * sidebar — built independently and agreeing on almost everything, which is
 * the shape of a component that should have existed.
 *
 * Where they disagreed is more interesting than where they matched. The main
 * sidebar tinted an active item's icon with `text-primary`; settings did not.
 * Neither is wrong on its own; having both is. The tint stays, because an
 * active row is worth seeing at a glance and settings simply never got it.
 */

/**
 * The icon's hover and press animation.
 *
 * Both sidebars drove this from React state — two `useState`s and four mouse
 * handlers each, roughly fifteen lines per sidebar to make an icon wiggle.
 * `group-hover` and `group-active` do the same thing in CSS, and do it for
 * keyboard focus and touch as well, which the mouse handlers never did.
 *
 * 20px, as the dashboard's Figma draws the sidebar (17:2777). The icon takes
 * the row's colour: the Figma tells the current page apart by brightness
 * alone, so an active icon is no longer tinted.
 */
export const navItemIconRecipe = cva([
  uiMarker,
  'size-icon-md shrink-0 transition-all duration-300 ease-out',
  'group-hover:scale-110 group-hover:rotate-3',
  'group-active:scale-75 group-active:-rotate-6',
]);

/*
 * The colour a sidebar row is drawn in, shared by the expanded rows and the
 * collapsed icon rail.
 *
 * The Figma draws the sidebar flat on the ground, with no card under the
 * current page: it is full black and every other row is half black, which
 * is what `muted-foreground` is in the brand. Pointing at a row brings it up
 * to full. Button's ghost wash is cancelled, because a grey pill appearing
 * under a row the design keeps flat would be the one surface in the sidebar.
 *
 * The `data-[force=hover]` twins are not optional: that attribute is how the
 * gallery shows a hover state without a mouse, and without them the catalogue
 * would show Button's ghost wash while the product shows none.
 */
const NAV_SURFACE = {
  true: [
    'text-foreground',
    'hover:bg-transparent hover:text-foreground',
    'data-[force=hover]:bg-transparent data-[force=hover]:text-foreground',
  ].join(' '),
  false: [
    'text-muted-foreground',
    'hover:bg-transparent hover:text-foreground',
    'data-[force=hover]:bg-transparent data-[force=hover]:text-foreground',
  ].join(' '),
} as const;

/** For a sidebar control that is not a NavItem — the collapsed rail's icons. */
export const navSurfaceRecipe = cva([uiMarker], {
  variants: { active: NAV_SURFACE },
  defaultVariants: { active: false },
});

/**
 * A row: 28 high, the icon 4px from its label, no inset of its own, so the
 * icons line up with the wordmark above them. Labels are 16px medium.
 */
export const navItemRecipe = cva(
  [uiMarker, 'group h-7 w-full justify-start gap-1 px-0'],
  {
    variants: {
      active: NAV_SURFACE,
      /** A link inside a group (Site's Builder, Bookings): a size down. */
      nested: {
        true: 'text-body-strong',
        false: 'text-body-lg-strong',
      },
      /** Dimmed without being disabled — a hidden item in the sidebar's edit mode. */
      muted: {
        true: 'opacity-50',
        false: '',
      },
    },
    defaultVariants: { active: false, nested: false, muted: false },
  }
);

export type NavItemRecipeProps = VariantProps<typeof navItemRecipe>;

/*
 * BREADCRUMBS
 *
 * Where the page is, along the top of the dashboard: "Call Log • CL-1042".
 * The Figma (17:2876) sets every crumb the same, 12px medium at half black,
 * with a dot between them, so the trail reads as a quiet address rather than
 * a row of links. A crumb that leads somewhere comes up to full black under
 * the pointer.
 */
export const breadcrumbsRecipe = cva([
  uiMarker,
  'flex min-w-0 items-center gap-2 font-sans text-caption-strong text-muted-foreground',
]);

/** One crumb. Shrinks before the ones beside it, so a long record name truncates. */
export const breadcrumbItemRecipe = cva([uiMarker, 'min-w-0 truncate']);

/** A crumb that is a link: the earlier sections of the trail. */
export const breadcrumbLinkRecipe = cva([
  uiMarker,
  'rounded-sm hover:text-foreground',
  transitionBase,
  focusRing,
]);

/**
 * The company and the person at the foot of the sidebar, each a row that
 * opens a menu: no surface and no inset, as the Figma draws them, so they
 * line up with the nav above. The pointer dims the row a touch rather than
 * laying a wash under it, which the flat sidebar has nowhere else.
 */
export const sidebarIdentityRecipe = cva([
  uiMarker,
  'flex w-full min-w-0 items-center gap-2 rounded-md text-left text-foreground',
  'hover:opacity-80 data-[state=open]:opacity-80',
  transitionBase,
  focusRing,
]);

/*
 * THE SIDEBAR'S FRAME
 *
 * The dashboard's Figma (17:2777): history arrows and the collapse toggle
 * along the top, level with the breadcrumbs; the wordmark; the pages; and
 * the company and the person at the foot. Flat on the ground, no rule down
 * its edge, 16px in from each side.
 */
export const sidebarFrameRecipe = cva([
  uiMarker,
  // The face and colour are restated here, not only left to AppShell,
  // because on a phone the sidebar opens in a sheet, which portals to
  // <body>, outside it.
  'flex h-full w-full flex-col bg-canvas font-sans text-foreground',
]);

/** Everything above the foot. Scrolls on its own when the pages run long. */
export const sidebarPagesRecipe = cva([
  uiMarker,
  'flex min-h-0 grow flex-col gap-6 overflow-y-auto px-4 pb-4',
]);

/** The arrows and the toggle: 24 high, 8 down, so they line up with the crumbs. */
export const sidebarToolbarRecipe = cva([
  uiMarker,
  'flex h-8 shrink-0 items-center justify-between gap-2 pt-2',
]);

/** The company and the person, pinned to the bottom. */
export const sidebarFooterRecipe = cva([
  uiMarker,
  'flex shrink-0 flex-col gap-4 p-4',
]);
